import React, { useEffect, useState } from 'react';
import { positionCaption } from '../utils/sharing';
import { useParams, useNavigate } from 'react-router-dom';
import { Layout } from '../components/layout';
import { Button, Badge } from '../components/ui';
import { api } from '../services/api';
import { Order, OrderStatus } from '../types';
import { formatPrice, formatDate } from '../utils/helpers';
import { useSettingsStore, useCartStore } from '../store';
import { useTelegram } from '../hooks/useTelegram';
import { CodeInstructions } from '../components/CodeInstructions';

const STATUS_MAP: Record<OrderStatus, { variant: 'success' | 'warning' | 'error' | 'info'; text: string }> = {
  completed: { variant: 'success', text: 'Выполнен' },
  ready: { variant: 'success', text: 'Готов' },
  awaiting_payment: { variant: 'warning', text: 'Ожидает оплаты' },
  pending: { variant: 'warning', text: 'Ожидает' },
  processing: { variant: 'info', text: 'В обработке' },
  manual: { variant: 'warning', text: 'Ручная обработка' },
  failed: { variant: 'error', text: 'Ошибка' },
  cancelled: { variant: 'error', text: 'Отменен' },
};

const REGION_FLAGS: Record<string, string> = { TR: '🇹🇷', IN: '🇮🇳', UA: '🇺🇦' };

// Пока заказ ждет оплату или выдачу, перечитываем его: первые минуты часто — коды
// поставщика обычно приходят за десятки секунд, дальше реже. Через полчаса бэкенд
// сам переводит зависшую выдачу в ручную, так что дольше опрашивать незачем.
const POLL_FAST_MS = 4000;
const POLL_SLOW_MS = 15000;
const POLL_FAST_FOR_MS = 3 * 60 * 1000;
const POLL_LIMIT_MS = 30 * 60 * 1000;

type OrderPhase = 'done' | 'awaiting_payment' | 'delivering' | 'manual' | 'failed' | 'cancelled';

/**
 * Состояние заказа глазами покупателя. Статус оплаченного заказа — «в обработке» и
 * при автовыдаче, и при ручной: ручную видно только по статусу выдачи. Правило то же,
 * что на сайте в lib/order-phase.ts.
 */
function orderPhase(order: Order): OrderPhase {
  const hasData = (order.codes?.length ?? 0) > 0 || Boolean(order.account);
  if (hasData || order.status === 'completed') return 'done';
  if (order.status === 'cancelled') return 'cancelled';
  if (order.status === 'failed') return 'failed';
  if (order.status === 'awaiting_payment') return 'awaiting_payment';
  if (order.status === 'manual' || order.fulfillmentStatus === 'manual' || order.fulfillmentStatus === 'failed') return 'manual';
  return 'delivering';
}

export const OrderStatusPage: React.FC = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const { settings } = useSettingsStore();
  const { openTelegramLink } = useTelegram();
  const clearRegionCart = useCartStore((s) => s.clearRegionCart);
  const [order, setOrder] = useState<Order | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);
  const [copiedOrder, setCopiedOrder] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);

  useEffect(() => {
    if (!orderId) { navigate('/'); return; }
    setIsLoading(true);

    // Выдача идет в фоне: заказ перечитываем сами, пока он не придет в конечное
    // состояние, иначе код виден только после перезахода на страницу.
    let stopped = false;
    let first = true;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const startedAt = Date.now();

    const scheduleNext = () => {
      const elapsed = Date.now() - startedAt;
      if (stopped || elapsed > POLL_LIMIT_MS) return;
      timer = setTimeout(load, elapsed < POLL_FAST_FOR_MS ? POLL_FAST_MS : POLL_SLOW_MS);
    };

    const load = async () => {
      const isFirst = first;
      first = false;
      try {
        const data = await api.getOrder(orderId);
        if (stopped) return;
        if (!data) {
          if (isFirst) navigate('/');
          return;
        }
        setOrder(data);
        if (isFirst && data.region) clearRegionCart(data.region);
        const phase = orderPhase(data);
        if (phase === 'awaiting_payment' || phase === 'delivering') scheduleNext();
      } catch {
        if (stopped) return;
        if (isFirst) navigate('/');
        else scheduleNext();
      } finally {
        if (isFirst && !stopped) setIsLoading(false);
      }
    };

    load();
    return () => {
      stopped = true;
      clearTimeout(timer);
    };
  }, [orderId]);

  const handleCopyCode = (code: string, index: number) => {
    navigator.clipboard.writeText(code);
    setCopiedIndex(index);
    setTimeout(() => setCopiedIndex(null), 2000);
  };

  const handleCopyField = (value: string, field: string) => {
    navigator.clipboard.writeText(value);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const handleContactSupport = () => {
    if (settings.supportUrl) openTelegramLink(settings.supportUrl);
  };

  if (isLoading || !order) {
    return <Layout showNav={false}><div /></Layout>;
  }

  const hasCodes = order.codes && order.codes.length > 0;
  const hasAccount = !!order.account;
  const isCompleted = order.status === 'completed';
  const isFailed = order.status === 'failed';
  const isCancelled = order.status === 'cancelled';
  const isAwaitingPayment = order.status === 'awaiting_payment';
  const phase = orderPhase(order);
  const statusInfo = STATUS_MAP[order.status] || { variant: 'warning' as const, text: order.status };

  return (
    <Layout showNav={false}>
      <div className="px-4 pt-3 pb-32">
        <div className="max-w-2xl mx-auto">

          {/* Status icon + title */}
          <div className="text-center mb-5">
            <div className="mb-3 flex justify-center">
              <div className={`h-14 w-14 rounded-2xl flex items-center justify-center ${hasCodes || hasAccount || isCompleted ? 'bg-emerald-50' : isFailed || isCancelled ? 'bg-red-50' : isAwaitingPayment ? 'bg-blue-50' : 'bg-amber-50'}`}>
                {hasCodes || hasAccount || isCompleted ? (
                  <svg className="h-7 w-7 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                ) : isFailed || isCancelled ? (
                  <svg className="h-7 w-7 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                ) : isAwaitingPayment ? (
                  <svg className="h-7 w-7 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                  </svg>
                ) : (
                  <svg className="h-7 w-7 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                )}
              </div>
            </div>
            <h1 style={{ fontFamily: 'Manrope, sans-serif', fontWeight: 800, fontSize: '22px', lineHeight: '28px', color: '#1A1D1A' }}>
              {hasCodes || hasAccount ? 'Заказ выполнен' : isCancelled ? 'Заказ отменен' : isFailed ? 'Ошибка оплаты' : isAwaitingPayment ? 'Ожидает оплаты' : 'Оплата прошла'}
            </h1>
            <p style={{ fontFamily: 'Manrope, sans-serif', fontWeight: 400, fontSize: '14px', lineHeight: '20px', color: '#818384', marginTop: '4px' }}>
              {hasCodes || hasAccount ? 'Данные для активации готовы' : isCancelled ? 'Заказ был отменен' : isFailed ? 'Обратитесь в поддержку для решения проблемы' : isAwaitingPayment ? 'Нажмите кнопку ниже для оплаты заказа' : phase === 'delivering' ? 'Выдаем заказ' : ((order.region as string) === 'SH' || phase === 'manual') ? 'Выдача вручную — напишите нам в поддержку' : 'Заказ будет выполнен за 10-15 минут'}
            </p>
          </div>

          {/* Order info card */}
          <div className="rounded-2xl p-4 mb-3" style={{ background: '#F4F4F7' }}>
            <div className="space-y-2.5">
              <div className="flex justify-between items-center">
                <span style={{ fontFamily: 'Manrope, sans-serif', fontWeight: 400, fontSize: '14px', color: '#818384' }}>Номер заказа</span>
                <button
                  className="flex items-center gap-1.5 active:scale-95 transition-transform"
                  onClick={() => { navigator.clipboard.writeText(order.orderNumber); setCopiedOrder(true); setTimeout(() => setCopiedOrder(false), 2000); }}
                >
                  <span style={{ fontFamily: 'Manrope, sans-serif', fontWeight: 700, fontSize: '14px', color: '#1A1D1A' }}>{order.orderNumber}</span>
                  {copiedOrder ? (
                    <svg className="w-3.5 h-3.5 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                  ) : (
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="#818384" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
                  )}
                </button>
              </div>
              <div className="flex justify-between items-center">
                <span style={{ fontFamily: 'Manrope, sans-serif', fontWeight: 400, fontSize: '14px', color: '#818384' }}>Дата</span>
                <span style={{ fontFamily: 'Manrope, sans-serif', fontWeight: 500, fontSize: '14px', color: '#1A1D1A' }}>{formatDate(order.createdAt)}</span>
              </div>
              <div className="flex justify-between items-center">
                <span style={{ fontFamily: 'Manrope, sans-serif', fontWeight: 400, fontSize: '14px', color: '#818384' }}>Сумма</span>
                <span style={{ fontFamily: 'Manrope, sans-serif', fontWeight: 700, fontSize: '14px', color: '#1A1D1A' }}>{formatPrice(order.totalAmount)}</span>
              </div>
              <div className="flex justify-between items-center">
                <span style={{ fontFamily: 'Manrope, sans-serif', fontWeight: 400, fontSize: '14px', color: '#818384' }}>Статус</span>
                <Badge variant={statusInfo.variant} size="md">{statusInfo.text}</Badge>
              </div>
            </div>
          </div>

          {/* Автовыдача идет: страница обновится сама */}
          {phase === 'delivering' && (
            <div className="rounded-2xl p-3.5 mb-3 flex items-start gap-2.5" style={{ background: '#EEF4FF' }}>
              <div className="w-5 h-5 mt-0.5 flex-shrink-0 rounded-full animate-spin" style={{ border: '2px solid #3657F0', borderTopColor: 'transparent' }} />
              <div>
                <p style={{ fontFamily: 'Manrope, sans-serif', fontWeight: 500, fontSize: '13px', lineHeight: '18px', color: '#1E3A8A' }}>
                  Оплата получена, выдаем заказ
                </p>
                <p style={{ fontFamily: 'Manrope, sans-serif', fontWeight: 400, fontSize: '13px', lineHeight: '18px', color: '#1D4ED8', marginTop: '2px' }}>
                  Все появится на этой странице автоматически, обновлять ее не нужно.
                </p>
              </div>
            </div>
          )}

          {/* Info notice for manual orders — только когда выдачу действительно передали в ручную */}
          {phase === 'manual' && (
            <div className="rounded-2xl p-3.5 mb-3 flex items-start gap-2.5" style={{ background: '#FFF8EB' }}>
              <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{ background: '#F59E0B' }}>
                <span className="text-white text-xs font-bold">!</span>
              </div>
              <div>
                <p style={{ fontFamily: 'Manrope, sans-serif', fontWeight: 500, fontSize: '13px', lineHeight: '18px', color: '#92400E' }}>
                  Ваш заказ был успешно оплачен!
                </p>
                <p style={{ fontFamily: 'Manrope, sans-serif', fontWeight: 400, fontSize: '13px', lineHeight: '18px', color: '#B45309', marginTop: '2px' }}>
                  Напишите, пожалуйста, нам в поддержку, чтобы мы могли оформить ваш заказ вручную.
                </p>
              </div>
            </div>
          )}

          {/* Activation codes */}
          {hasCodes && (
            <div className="mb-3">
              <h2 style={{ fontFamily: 'Manrope, sans-serif', fontWeight: 700, fontSize: '16px', color: '#1A1D1A', marginBottom: '10px' }}>
                Коды активации
              </h2>
              <div className="space-y-2.5">
                {order.codes!.map((codeItem, index) => (
                  <div key={index} className="rounded-2xl p-3.5 border border-gray-200/80 bg-white">
                    <p style={{ fontFamily: 'Manrope, sans-serif', fontWeight: 600, fontSize: '13px', color: '#1A1D1A', marginBottom: '8px' }}>
                      {codeItem.productName}
                    </p>
                    <div className="flex items-center justify-between rounded-xl p-3" style={{ background: '#F4F4F7' }}>
                      <code style={{ fontFamily: 'JetBrains Mono, monospace', fontWeight: 600, fontSize: '15px', color: '#3657F0' }}>
                        {codeItem.code}
                      </code>
                      <button
                        onClick={() => handleCopyCode(codeItem.code, index)}
                        className="p-1.5 rounded-lg active:scale-95 transition-transform"
                        style={{ background: copiedIndex === index ? '#D1FAE5' : 'transparent' }}
                      >
                        {copiedIndex === index ? (
                          <svg className="w-5 h-5 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                          </svg>
                        ) : (
                          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="#818384" strokeWidth={1.8}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                          </svg>
                        )}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              <CodeInstructions approute={order.approuteData} />
            </div>
          )}

          {/* Account details */}
          {hasAccount && order.account && (
            <div className="mb-3">
              <h2 style={{ fontFamily: 'Manrope, sans-serif', fontWeight: 700, fontSize: '16px', color: '#1A1D1A', marginBottom: '10px' }}>
                Данные аккаунта
              </h2>
              <div className="rounded-2xl p-4 border border-gray-200/80 bg-white space-y-3">
                {/* Login */}
                <div className="flex justify-between items-center gap-2">
                  <span style={{ fontFamily: 'Manrope, sans-serif', fontWeight: 400, fontSize: '13px', color: '#818384', flexShrink: 0 }}>Логин</span>
                  <button className="flex items-center gap-1.5 active:scale-95 transition-transform min-w-0" onClick={() => handleCopyField(order.account!.email, 'email')}>
                    <span className="truncate" style={{ fontFamily: 'JetBrains Mono, monospace', fontWeight: 600, fontSize: '13px', color: '#1A1D1A' }}>{order.account.email}</span>
                    {copiedField === 'email' ? (
                      <svg className="w-3.5 h-3.5 flex-shrink-0 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                    ) : (
                      <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="#818384" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
                    )}
                  </button>
                </div>
                {/* Password */}
                <div className="flex justify-between items-center gap-2">
                  <span style={{ fontFamily: 'Manrope, sans-serif', fontWeight: 400, fontSize: '13px', color: '#818384', flexShrink: 0 }}>Пароль</span>
                  <button className="flex items-center gap-1.5 active:scale-95 transition-transform" onClick={() => handleCopyField(order.account!.password, 'password')}>
                    <span style={{ fontFamily: 'JetBrains Mono, monospace', fontWeight: 600, fontSize: '13px', color: '#1A1D1A' }}>{order.account.password}</span>
                    {copiedField === 'password' ? (
                      <svg className="w-3.5 h-3.5 flex-shrink-0 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                    ) : (
                      <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="#818384" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
                    )}
                  </button>
                </div>
                {/* DOB */}
                {order.account.dob && (
                  <div className="flex justify-between items-center gap-2">
                    <span style={{ fontFamily: 'Manrope, sans-serif', fontWeight: 400, fontSize: '13px', color: '#818384', flexShrink: 0 }}>Дата рождения</span>
                    <button className="flex items-center gap-1.5 active:scale-95 transition-transform" onClick={() => handleCopyField(order.account!.dob!, 'dob')}>
                      <span style={{ fontFamily: 'Manrope, sans-serif', fontWeight: 600, fontSize: '13px', color: '#1A1D1A' }}>{order.account.dob}</span>
                      {copiedField === 'dob' ? (
                        <svg className="w-3.5 h-3.5 flex-shrink-0 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                      ) : (
                        <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="#818384" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
                      )}
                    </button>
                  </div>
                )}
                {/* Backup codes */}
                {order.account.backupCodes && (
                  <div>
                    <div className="flex justify-between items-center mb-1.5">
                      <span style={{ fontFamily: 'Manrope, sans-serif', fontWeight: 400, fontSize: '13px', color: '#818384' }}>Резервные коды</span>
                      <button className="p-1 active:scale-95 transition-transform" onClick={() => handleCopyField(order.account!.backupCodes!, 'backup')}>
                        {copiedField === 'backup' ? (
                          <svg className="w-4 h-4 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                        ) : (
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="#818384" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
                        )}
                      </button>
                    </div>
                    <div className="rounded-xl p-3" style={{ background: '#F4F4F7' }}>
                      <code style={{ fontFamily: 'JetBrains Mono, monospace', fontWeight: 500, fontSize: '12px', color: '#1A1D1A', wordBreak: 'break-all', lineHeight: '18px' }}>
                        {order.account.backupCodes}
                      </code>
                    </div>
                  </div>
                )}
                {/* 2FA key */}
                {order.account.twoFactorKey && (
                  <div>
                    <div className="flex justify-between items-center mb-1.5">
                      <span style={{ fontFamily: 'Manrope, sans-serif', fontWeight: 400, fontSize: '13px', color: '#818384' }}>Ключ 2FA</span>
                      <button className="p-1 active:scale-95 transition-transform" onClick={() => handleCopyField(order.account!.twoFactorKey!, '2fa')}>
                        {copiedField === '2fa' ? (
                          <svg className="w-4 h-4 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                        ) : (
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="#818384" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
                        )}
                      </button>
                    </div>
                    <div className="rounded-xl p-3" style={{ background: '#F4F4F7' }}>
                      <code style={{ fontFamily: 'JetBrains Mono, monospace', fontWeight: 500, fontSize: '11px', color: '#3657F0', wordBreak: 'break-all', lineHeight: '16px' }}>
                        {order.account.twoFactorKey}
                      </code>
                    </div>
                  </div>
                )}
                {/* Instructions */}
                {order.account.instructions && (
                  <div className="rounded-xl p-3" style={{ background: '#FFF8EB' }}>
                    <p style={{ fontFamily: 'Manrope, sans-serif', fontWeight: 400, fontSize: '12px', lineHeight: '18px', color: '#92400E', whiteSpace: 'pre-line' }}>
                      {order.account.instructions}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Order items */}
          <div>
            <h2 style={{ fontFamily: 'Manrope, sans-serif', fontWeight: 700, fontSize: '16px', color: '#1A1D1A', marginBottom: '10px' }}>
              Товары
            </h2>
            {(order.steamLogin || order.orderNumber.startsWith('ST-')) ? (
              // Заказ «Пополнение Steam»: вместо items — карточка с суммой зачисления и комиссией
              <div className="rounded-2xl p-3" style={{ background: '#F4F4F7' }}>
                <div className="flex items-center gap-3">
                  <img
                    src="/services/logo-steam.webp"
                    alt=""
                    className="w-14 h-14 object-cover rounded-xl flex-shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <p style={{ fontFamily: 'Manrope, sans-serif', fontWeight: 600, fontSize: '14px', lineHeight: '18px', color: '#1A1D1A' }}>
                      Пополнение Steam на {formatPrice(Math.round(order.totalRegional || 0))}
                    </p>
                    {order.steamLogin && (
                      <p className="mt-0.5" style={{ fontFamily: 'Manrope, sans-serif', fontWeight: 500, fontSize: '13px', color: '#818384' }}>
                        Логин: {order.steamLogin}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex items-center justify-between mt-3 pt-3" style={{ borderTop: '1px solid #E1E3E5' }}>
                  <span style={{ fontFamily: 'Manrope, sans-serif', fontWeight: 500, fontSize: '13px', color: '#818384' }}>
                    Комиссия за пополнение
                  </span>
                  <span style={{ fontFamily: 'Manrope, sans-serif', fontWeight: 600, fontSize: '13px', color: '#1A1D1A' }}>
                    {formatPrice(Math.max(0, order.totalAmount - Math.round(order.totalRegional || 0)))}
                  </span>
                </div>
              </div>
            ) : (
            <div className="space-y-2">
              {order.items.map((item, index) => (
                <div key={index} className="flex items-center gap-3 rounded-2xl p-3" style={{ background: '#F4F4F7' }}>
                  <img
                    src={item.product.coverImage}
                    alt={item.product.name}
                    className="w-14 h-14 object-cover rounded-xl flex-shrink-0"
                    onError={(e) => { (e.target as HTMLImageElement).src = 'https://via.placeholder.com/64?text=PS'; }}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="line-clamp-2" style={{ fontFamily: 'Manrope, sans-serif', fontWeight: 600, fontSize: '14px', lineHeight: '18px', color: '#1A1D1A' }}>
                      {item.product.name}
                    </p>
                    {item.variant?.position && (
                      <p style={{ fontSize: '12px', lineHeight: '16px', color: '#818384' }}>{positionCaption(item.product, item.variant)}</p>
                    )}
                    <div className="flex items-center gap-1.5 mt-1">
                      {item.variant?.region && (
                        <span className="text-xs">{REGION_FLAGS[item.variant.region]}</span>
                      )}
                      <span style={{ fontFamily: 'Manrope, sans-serif', fontWeight: 500, fontSize: '13px', color: '#818384' }}>
                        {formatPrice(item.variant.price)}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            )}
          </div>

        </div>
      </div>

      {/* Fixed bottom actions */}
      <div className="fixed left-0 right-0 bottom-0 z-30 px-4 pt-3 bg-white/95 backdrop-blur-lg" style={{ paddingBottom: 'max(12px, env(safe-area-inset-bottom, 0px))' }}>
        <div className="max-w-2xl mx-auto space-y-2">
          {isAwaitingPayment && order.paymentUrl ? (
            <>
              <Button onClick={() => navigate(`/payment/${order.id}`, { state: { paymentUrl: order.paymentUrl } })} variant="primary" fullWidth size="md">
                Оплатить
              </Button>
              <Button onClick={() => navigate(-1 as any)} variant="secondary" fullWidth size="md">
                Назад
              </Button>
            </>
          ) : (
            <>
              <Button onClick={handleContactSupport} variant="primary" fullWidth size="md">
                Написать в поддержку
              </Button>
              <Button onClick={() => navigate(hasCodes || hasAccount || isCompleted ? '/' : -1 as any)} variant="secondary" fullWidth size="md">
                {hasCodes || hasAccount || isCompleted ? 'На главную' : 'Назад'}
              </Button>
            </>
          )}
        </div>
      </div>
    </Layout>
  );
};
